import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { appTheme } from '../styles/theme';
import {
  LANGUAGE_QUERY_PARAM,
  SUPPORTED_LANGUAGES,
  SupportedLanguageCode,
} from '../i18n/languages';
import { setSkipUrlLanguageApply } from '../i18n/languageUrlState';

const Select = styled.select<{ $compact?: boolean }>`
  padding: ${({ $compact }) => ($compact ? '6px 28px 6px 10px' : '8px 32px 8px 12px')};
  border-radius: ${appTheme.radiusPill};
  border: 1px solid ${appTheme.border};
  background: ${appTheme.surface};
  color: ${appTheme.text};
  font-family: inherit;
  font-size: ${({ $compact }) => ($compact ? '0.75rem' : '0.8125rem')};
  font-weight: 600;
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%235C7168' d='M2.5 4.5 6 8l3.5-3.5'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 10px center;

  &:hover {
    border-color: ${appTheme.green};
    background-color: ${appTheme.surfaceHover};
  }

  &:focus-visible {
    outline: 2px solid ${appTheme.green};
    outline-offset: 2px;
  }
`;

interface LanguageSwitcherProps {
  compact?: boolean;
  className?: string;
}

const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({ compact = false, className }) => {
  const { i18n, t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentLanguage = (i18n.resolvedLanguage?.split('-')[0] ?? 'en') as SupportedLanguageCode;

  return (
    <Select
      className={className}
      $compact={compact}
      value={currentLanguage}
      aria-label={t('language.label')}
      onChange={(event) => {
        const language = event.target.value as SupportedLanguageCode;
        setSkipUrlLanguageApply();

        const nextParams = new URLSearchParams(searchParams);
        nextParams.set(LANGUAGE_QUERY_PARAM, language);
        setSearchParams(nextParams, { replace: true });
        void i18n.changeLanguage(language);
      }}
    >
      {SUPPORTED_LANGUAGES.map((language) => (
        <option key={language.code} value={language.code}>
          {t(language.labelKey)}
        </option>
      ))}
    </Select>
  );
};

export default LanguageSwitcher;
